import { useState } from "react";
import { useAuth } from "../AuthContext";
import { load } from "@cashfreepayments/cashfree-js";

const BACKEND = window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://myprofilecorner.com/api";

const PLANS = [
    {
        id: "monthly",
        label: "Monthly",
        price: "₹49",
        period: "/ month",
        saving: null,
        highlight: false,
    },
    {
        id: "yearly",
        label: "Yearly",
        price: "₹299",
        period: "/ year",
        saving: "Save ₹289",
        highlight: true,
    },
];

const PERKS = [
    "200+ premium card themes",
    "Custom fonts & color palettes",
    "Priority support",
    "Early access to new features",
    "Remove 'MyProfileCorner' branding",
];

export default function UpgradeModal({ onClose }) {
    const { user } = useAuth();
    const [selectedPlan, setSelectedPlan] = useState("yearly");
    const [name, setName] = useState(user?.displayName || "");
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleUpgrade = async () => {
        if (!user) return;
        if (!name.trim()) { setError("Please enter your name."); return; }
        const cleanPhone = phone.replace(/\D/g, "");
        if (cleanPhone.length < 10) { setError("Please enter a valid 10-digit phone number."); return; }
        setLoading(true);
        setError("");

        try {
            // Step 1: Create order on backend
            const res = await fetch(`${BACKEND}/create-order`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    userId: user.uid,
                    plan: selectedPlan,
                    customerName: name.trim() || user.displayName || "User",
                    customerEmail: user.email,
                    customerPhone: cleanPhone.slice(-10),
                }),
            });

            const data = await res.json();
            if (!res.ok || !data.paymentSessionId) {
                const detail = data.details?.message || data.details?.type || JSON.stringify(data.details) || "";
                throw new Error(detail ? `${data.error}: ${detail}` : (data.error || "Failed to create order"));
            }

            // Step 2: Load Cashfree JS SDK — use same mode the backend used to create the order
            const cashfree = await load({
                mode: data.mode || "production",
            });

            localStorage.setItem("paymentReturnPath", window.location.pathname);
            cashfree.checkout({
                paymentSessionId: data.paymentSessionId,
                redirectTarget: "_self",
            });

        } catch (err) {
            console.error("Payment error:", err);
            setError(err.message || "Something went wrong. Please try again.");
            setLoading(false);
        }
    };

    return (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }} onClick={onClose}>
            <div style={{ background: "#fff", borderRadius: "28px", width: "100%", maxWidth: "400px", overflow: "hidden", boxShadow: "0 24px 80px rgba(0,0,0,0.25)" }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{ background: "linear-gradient(135deg, #0D0B1A 0%, #1A1535 60%, #0AADA1 200%)", padding: "28px 24px 24px", position: "relative" }}>
                    <button onClick={onClose} style={{ position: "absolute", top: "16px", right: "16px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "50%", width: "30px", height: "30px", color: "#fff", fontSize: "16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                    <div style={{ fontSize: "28px", marginBottom: "8px" }}>✨</div>
                    <h2 style={{ margin: "0 0 6px", color: "#fff", fontSize: "22px", fontWeight: "800" }}>Go Premium</h2>
                    <p style={{ margin: 0, color: "rgba(255,255,255,0.55)", fontSize: "13px" }}>Unlock 200+ themes & exclusive features</p>
                </div>

                <div style={{ padding: "24px" }}>

                    {/* Perks */}
                    <div style={{ marginBottom: "20px" }}>
                        {PERKS.map(perk => (
                            <div key={perk} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                                <div style={{ width: "18px", height: "18px", borderRadius: "50%", background: "linear-gradient(135deg,#0AADA1,#0f8f85)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                </div>
                                <span style={{ fontSize: "13px", color: "#374151", fontWeight: "500" }}>{perk}</span>
                            </div>
                        ))}
                    </div>

                    {/* Plan selector */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "20px" }}>
                        {PLANS.map(plan => (
                            <button
                                key={plan.id}
                                onClick={() => setSelectedPlan(plan.id)}
                                style={{
                                    padding: "14px 10px",
                                    borderRadius: "16px",
                                    border: selectedPlan === plan.id ? "2px solid #0AADA1" : "2px solid #e5e7eb",
                                    background: selectedPlan === plan.id ? "rgba(10,173,161,0.06)" : "#f9fafb",
                                    cursor: "pointer",
                                    textAlign: "center",
                                    position: "relative",
                                    transition: "all 0.18s",
                                }}
                            >
                                {plan.highlight && (
                                    <div style={{ position: "absolute", top: "-9px", left: "50%", transform: "translateX(-50%)", background: "linear-gradient(135deg,#f59e0b,#d97706)", color: "#fff", fontSize: "9px", fontWeight: "800", padding: "2px 8px", borderRadius: "20px", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>BEST VALUE</div>
                                )}
                                <div style={{ fontSize: "11px", fontWeight: "700", color: "#6b7280", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{plan.label}</div>
                                <div style={{ fontSize: "22px", fontWeight: "800", color: "#111", lineHeight: 1 }}>{plan.price}</div>
                                <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: "2px" }}>{plan.period}</div>
                                {plan.saving && <div style={{ fontSize: "10px", color: "#0AADA1", fontWeight: "700", marginTop: "4px" }}>{plan.saving}</div>}
                            </button>
                        ))}
                    </div>

                    {/* Name */}
                    <div style={{ marginBottom: "12px" }}>
                        <label style={{ display: "block", fontSize: "11px", fontWeight: "700", color: "#6b7280", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: "6px" }}>Full Name</label>
                        <input
                            type="text"
                            placeholder="Name shown on payment page"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            style={{ width: "100%", padding: "12px 14px", border: "1.5px solid #e5e7eb", borderRadius: "12px", background: "#f9fafb", fontSize: "14px", color: "#111", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                        />
                    </div>

                    {/* Phone number */}
                    <div style={{ marginBottom: "16px" }}>
                        <label style={{ display: "block", fontSize: "11px", fontWeight: "700", color: "#6b7280", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: "6px" }}>Mobile Number</label>
                        <div style={{ display: "flex", alignItems: "center", border: "1.5px solid #e5e7eb", borderRadius: "12px", overflow: "hidden", background: "#f9fafb", transition: "border-color 0.15s" }}>
                            <span style={{ padding: "12px 10px 12px 14px", fontSize: "14px", color: "#374151", fontWeight: "600", borderRight: "1px solid #e5e7eb", background: "#f3f4f6", flexShrink: 0 }}>+91</span>
                            <input
                                type="tel"
                                inputMode="numeric"
                                maxLength={10}
                                placeholder="10-digit mobile number"
                                value={phone}
                                onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                style={{ flex: 1, padding: "12px 14px", border: "none", background: "transparent", fontSize: "14px", color: "#111", outline: "none", fontFamily: "inherit" }}
                            />
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div style={{ background: "#fee2e2", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 12px", marginBottom: "14px", fontSize: "13px", color: "#dc2626" }}>
                            {error}
                        </div>
                    )}

                    {/* CTA */}
                    <button
                        onClick={handleUpgrade}
                        disabled={loading}
                        style={{ width: "100%", padding: "16px", borderRadius: "16px", border: "none", background: loading ? "#9ca3af" : "linear-gradient(135deg,#0AADA1,#0f8f85)", color: "#fff", fontSize: "16px", fontWeight: "800", cursor: loading ? "not-allowed" : "pointer", letterSpacing: "-0.2px", transition: "all 0.2s" }}
                    >
                        {loading ? "Opening checkout…" : `Upgrade for ${PLANS.find(p => p.id === selectedPlan)?.price}`}
                    </button>

                    <p style={{ textAlign: "center", fontSize: "11px", color: "#d1d5db", margin: "12px 0 0" }}>
                        Secure payment via Cashfree · Cancel anytime
                    </p>
                </div>
            </div>
        </div>
    );
}
